import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Chip,
  InputAdornment,
  Divider,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ContentCopy,
  PersonAdd,
  Delete,
  Share,
  CheckCircle,
  MoreVert,
  Edit,
  Visibility
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiService from '../utils/api';
import useRoadmapStore from '../store/roadmapStore';
import useAuthStore from '../store/authStore';

const ProjectSharing = () => {
  const { t } = useTranslation();
  const roadmapData = useRoadmapStore((state) => state.roadmapData);
  const currentRoadmapId = useRoadmapStore((state) => state.currentRoadmapId);
  const currentUser = useAuthStore((state) => state.user);
  
  const [email, setEmail] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const projectId = roadmapData?.project_id;
  const isOwner = owner && currentUser && (owner.id === currentUser.id || owner._id === currentUser.id || owner === currentUser.id);

  // Helper to extract user ID from various formats
  const getUserId = (userObj) => {
    if (!userObj) return null;
    return userObj.user?._id || userObj.user?.id || userObj._id || userObj.id || null;
  };

  useEffect(() => {
    if (currentRoadmapId) {
      loadSharedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoadmapId]);

  const loadSharedUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSharedUsers(currentRoadmapId);
      setOwner(data.owner);
      setSharedUsers(data.shared_with || []);
    } catch (err) {
      setError(t('sharing.errorLoadUsers'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyProjectId = () => {
    navigator.clipboard.writeText(projectId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!email) {
      setError(t('sharing.errorEmptyEmail'));
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await apiService.shareRoadmap(currentRoadmapId, email);
      setSuccess(t('sharing.successShared', { email }));
      setEmail('');
      await loadSharedUsers();
    } catch (err) {
      setError(err.message || t('sharing.errorShareFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userObj) => {
    const userId = getUserId(userObj);
    if (!userId) {
      setError(t('sharing.errorInvalidUser'));
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      await apiService.removeSharedUser(currentRoadmapId, userId);
      setSuccess(t('sharing.successRemoved'));
      await loadSharedUsers();
      handleCloseMenu();
    } catch (err) {
      setError(t('sharing.errorRemoveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeAccessLevel = async (userObj, newAccessLevel) => {
    const userId = getUserId(userObj);
    if (!userId) {
      setError(t('sharing.errorInvalidUser'));
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      await apiService.updateSharedUserAccess(currentRoadmapId, userId, newAccessLevel);
      const level = newAccessLevel === 'edit' ? t('sharing.canEdit') : t('sharing.viewOnly');
      setSuccess(t('sharing.successAccessUpdated', { level }));
      await loadSharedUsers();
      handleCloseMenu();
    } catch (err) {
      setError(t('sharing.errorUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  if (!currentRoadmapId) {
    return (
      <Alert severity="info">
        {t('sharing.saveFirst')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Project ID Section */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Share sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">{t('sharing.projectId')}</Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('sharing.shareIdHint')}
        </Typography>

        <TextField
          fullWidth
          value={projectId || t('sharing.notAvailable')}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? t('sharing.copied') : t('sharing.copyId')}>
                  <IconButton onClick={handleCopyProjectId} edge="end">
                    {copied ? <CheckCircle color="success" /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: 'primary.main'
            }
          }}
        />
      </Paper>

      {/* Team Access Section */}
      {isOwner && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonAdd sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('sharing.addMember')}</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label={t('sharing.memberEmail')}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="colleague@example.com"
              disabled={loading}
            />
            <Button
              variant="contained"
              onClick={handleShare}
              disabled={loading || !email}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAdd />}
              sx={{ minWidth: 120 }}
            >
              {t('sharing.share')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Team Members List */}
      <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('sharing.teamMembers')}
        </Typography>

        {loading && !sharedUsers.length ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {/* Owner */}
            {owner && (
              <>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {owner.name}
                        <Chip label={t('sharing.ownerLabel')} size="small" color="primary" />
                      </Box>
                    }
                    secondary={owner.email}
                  />
                </ListItem>
                {sharedUsers.length > 0 && <Divider />}
              </>
            )}

            {/* Shared Users */}
            {sharedUsers.map((item, index) => (
              <React.Fragment key={item.user?.id || index}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.user?.name || item.email}
                        <Chip 
                          label={item.access_level === 'edit' ? t('sharing.canEdit') : t('sharing.viewOnly')}
                          icon={item.access_level === 'edit' ? <Edit /> : <Visibility />}
                          size="small" 
                          color={item.access_level === 'edit' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={item.user?.email || item.email}
                  />
                  {isOwner && (
                    <ListItemSecondaryAction>
                      <Tooltip title={t('sharing.manageAccess')}>
                        <IconButton
                          edge="end"
                          onClick={(e) => handleOpenMenu(e, item)}
                          disabled={loading}
                          color="primary"
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
                {index < sharedUsers.length - 1 && <Divider />}
              </React.Fragment>
            ))}

            {sharedUsers.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={t('sharing.noMembersTitle')}
                  secondary={isOwner ? t('sharing.noMembersHintOwner') : t('sharing.noMembersHintMember')}
                  primaryTypographyProps={{ color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        )}
      </Paper>

      {/* Access Control Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            {t('sharing.changeAccessLevel')}
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleChangeAccessLevel(selectedUser, 'edit')}
          disabled={selectedUser?.access_level === 'edit'}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          {t('sharing.canEdit')}
        </MenuItem>
        <MenuItem 
          onClick={() => handleChangeAccessLevel(selectedUser, 'view')}
          disabled={selectedUser?.access_level === 'view'}
        >
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          {t('sharing.viewOnly')}
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleRemoveUser(selectedUser)}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          {t('sharing.removeAccess')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectSharing;
